import { z } from 'zod';
import { cuid, dinheiro, isoDate, paginacao } from './primitives.js';

export const componenteTipo = z.enum(['MATERIAL', 'MAO_DE_OBRA', 'AQUISICAO', 'OUTRO']);
/** Tipo como a API devolve: inclui COPRODUTO (linha única da ficha de um coproduto, criada pelo sistema; não se cria à mão). */
export type ComponenteTipoResposta = z.infer<typeof componenteTipo> | 'COPRODUTO';

/**
 * Perda na linha de material (Sessao 16). `quantidade` e o consumo LIQUIDO; o consumo total que o motor usa sai de `perdaModo` + `perdaValor`:
 *  - SOBRE_SAIDA (p, fracao)  : total = q x (1 + p)     ex.: 1,000 + 20% sobre a saida = 1,200
 *  - SOBRE_ENTRADA (p, fracao): total = q / (1 - p)     (p < 1)
 *  - RENDIMENTO (r, fracao)   : total = q / r           (0 < r <= 1)
 *  - CONSUMO_TOTAL (valor)    : total = o valor digitado (a quantidade e so nominal)
 * Etapa (centro de custo) nao tem perda por linha: o tempo recebe so o rendimento do processo da ficha.
 */
export const perdaModo = z.enum(['NENHUM', 'SOBRE_SAIDA', 'SOBRE_ENTRADA', 'RENDIMENTO', 'CONSUMO_TOTAL']);
export type PerdaModo = z.infer<typeof perdaModo>;
const decimalPerda = z.string().regex(/^\d+(\.\d{1,8})?$/, 'Valor inválido (decimal não negativo, até 8 casas)');

/** Grupo de insumos que recebe o rendimento do processo (alem do tempo das etapas). */
export const rendimentoGrupo = z.enum(['MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO', 'TODOS_INSUMOS']);
export type RendimentoGrupo = z.infer<typeof rendimentoGrupo>;

/** Ficha nova (versao seguinte do item); custoUnitario = soma dos componentes, recalculada a cada insercao. */
export const criarFichaSchema = z.object({
  itemId: cuid,
  descricao: z.string().trim().min(1).max(200).optional(),
});
export type CriarFichaDto = z.infer<typeof criarFichaSchema>;

/**
 * GET /fichas-tecnicas?itemId=...: lista as versoes do item. `comCustoHoje=true` (Sessao 49, C) acrescenta em cada versao `custoHoje`
 * (mesmo calculo do cabecalho, na data de hoje, sob demanda, sem gravar) + `estadoCusto`/`alertasCusto`; sem a flag a lista
 * e a de sempre (barata). A tela de versoes deve passar `comCustoHoje=true`.
 */
export const listarFichasSchema = paginacao.extend({ itemId: cuid, comCustoHoje: z.enum(['true', 'false']).transform((v) => v === 'true').optional() });
export type ListarFichasQuery = z.infer<typeof listarFichasSchema>;

/** GET /fichas-tecnicas/:id?data=AAAA-MM-DD: data em que o custo e resolvido (CPM do mes, escolhida do insumo). Default: hoje. */
export const consultaFichaSchema = z.object({ data: isoDate.optional() });
export type ConsultaFichaQuery = z.infer<typeof consultaFichaSchema>;

/** Linha de composicao (corpo sem a regra da descricao). custoTotal e derivado (quantidade x custoUnitario). */
const componenteBase = z.object({
  ordem: z.number().int().min(1).max(999),
  tipo: componenteTipo,
  /**
   * Item componente. So aceita item que COMPOE ficha (`compoeFichaTecnica`) ou semiacabado (PRODUTO/SERVICO com ficha escolhida);
   * senao 422 `COMPONENTE_NAO_PERMITIDO`. Item de tipo COMPRADO (MERCADORIA e insumos) em linha MATERIAL/AQUISICAO custa a
   * aquisicao VIGENTE na data (nada de custo copiado na linha).
   */
  componenteItemId: cuid.optional(),
  /** DESCONTINUADO (Sessao 6): a vigencia de aquisicao nao fica mais presa a linha. So serve para achar o item quando `componenteItemId` nao vem. */
  custoAquisicaoId: cuid.optional(),
  /**
   * ETAPA de roteiro: centro de custo + `quantidade` = MINUTOS. Exige tipo MAO_DE_OBRA (a unidade vira `min`). O custo e
   * minutos x CPM vigente do centro na data; `custoUnitario` informado numa etapa e ignorado.
   */
  centroCustoId: cuid.optional(),
  /**
   * Centro de consumo OPCIONAL de um material (onde ele e usado): analitico, informativo, nunca entra no custo. Exclusivo de
   * `centroCustoId` (a linha ou e etapa ou e material com centro de consumo).
   */
  centroConsumoId: cuid.optional(),
  /**
   * Material/hora: obrigatoria. ETAPA de roteiro (com `centroCustoId`): e a OBSERVACAO da etapa, opcional (vazia por padrao); a etapa se mostra
   * como "<ordem>. <centro>" (Sessao 37).
   */
  descricao: z.string().trim().max(200).default(''),
  unidade: z.string().trim().min(1).max(10).optional(),
  quantidade: dinheiro,
  /** Obrigatorio salvo insumo comprado (aquisicao vigente), etapa (CPM) e MATERIAL com insumo de ficha escolhida (cascata). */
  custoUnitario: dinheiro.optional(),
  /** Perda da linha (padrao NENHUM). `perdaValor` e obrigatorio fora de NENHUM. */
  perdaModo: perdaModo.optional(),
  perdaValor: decimalPerda.optional(),
  /** Sessao 17: fixa o fornecedor da aquisicao desta linha (insumo comprado); sem isto, vale a aquisicao preferencial. */
  fornecedorId: cuid.nullish(),
});
/** Descricao obrigatoria, salvo etapa de roteiro (com centro de custo): ai ela e so a observacao. */
const exigeDescricaoSeNaoEtapa = (c: { descricao: string; centroCustoId?: string | undefined }) => c.descricao.length > 0 || c.centroCustoId !== undefined;
const MENSAGEM_DESCRICAO = { message: 'Informe a descrição', path: ['descricao'] };

export const componenteSchema = componenteBase.refine(exigeDescricaoSeNaoEtapa, MENSAGEM_DESCRICAO);
export type ComponenteDto = z.infer<typeof componenteSchema>;

/** Linha no corpo de salvar/nova versao: a ordem e a posicao no array (1-based), definida pela API. */
export const componenteLoteSchema = componenteBase.omit({ ordem: true }).refine(exigeDescricaoSeNaoEtapa, MENSAGEM_DESCRICAO);
export type ComponenteLoteDto = z.infer<typeof componenteLoteSchema>;

/** Rendimento do processo: fracao decimal, 0 < r <= 1. */
export const rendimentoProcessoSchema = z.string().regex(/^(0\.\d{1,8}|1(\.0{1,8})?)$/, 'Rendimento deve ser maior que 0 e até 1 (fração)').refine((v) => Number(v) > 0, 'Rendimento deve ser maior que zero');

/** PATCH /fichas-tecnicas/:id/rendimento: rendimento do processo e o grupo de insumos que o recebe. */
export const rendimentoFichaSchema = z.object({ rendimentoProcesso: rendimentoProcessoSchema.nullable(), rendimentoGrupo: rendimentoGrupo.optional() });
export type RendimentoFichaDto = z.infer<typeof rendimentoFichaSchema>;

/** PUT /fichas-tecnicas/:id/componentes: troca TODAS as linhas de uma ficha ainda nao usada em snapshot. */
export const salvarComponentesSchema = z.object({
  descricao: z.string().trim().min(1).max(200).nullable().optional(),
  componentes: z.array(componenteLoteSchema).max(200),
  /** Rendimento do processo da ficha (fracao 0 < r <= 1; null = sem). Ausente = mantem o atual. */
  rendimentoProcesso: rendimentoProcessoSchema.nullable().optional(),
  rendimentoGrupo: rendimentoGrupo.optional(),
});
export type SalvarComponentesDto = z.infer<typeof salvarComponentesSchema>;

/** POST /fichas-tecnicas/:id/nova-versao: copia a ficha (ou usa `componentes`, se vier) como a proxima versao. */
export const novaVersaoSchema = z.object({
  descricao: z.string().trim().min(1).max(200).nullable().optional(),
  componentes: z.array(componenteLoteSchema).max(200).optional(),
});
export type NovaVersaoDto = z.infer<typeof novaVersaoSchema>;

/** Linha da simulacao: tolerante (rascunho de grade); a API classifica o que esta ausente ou invalido em `alertas`. */
export const componenteSimularSchema = z.object({
  tipo: componenteTipo,
  componenteItemId: cuid.optional(),
  custoAquisicaoId: cuid.optional(),
  centroCustoId: cuid.optional(),
  centroConsumoId: cuid.optional(),
  descricao: z.string().max(200).optional(),
  unidade: z.string().max(10).optional(),
  quantidade: z.string().max(40),
  custoUnitario: z.string().max(40).optional(),
  perdaModo: perdaModo.optional(),
  perdaValor: z.string().max(40).optional(),
  fornecedorId: cuid.nullish(),
});
export type ComponenteSimularDto = z.infer<typeof componenteSimularSchema>;

/**
 * POST /fichas-tecnicas/simular: recalcula sem gravar. `data` (default hoje) escolhe a competencia do CPM. `itemId` (o item da
 * ficha) e opcional: com ele a simulacao aplica as regras do TIPO do item (PRODUTO nao aceita mao de obra fora do processo).
 */
export const simularFichaSchema = z.object({
  data: isoDate.optional(),
  itemId: cuid.optional(),
  componentes: z.array(componenteSimularSchema).max(200),
  rendimentoProcesso: z.string().max(40).nullable().optional(),
  rendimentoGrupo: rendimentoGrupo.optional(),
});
export type SimularFichaDto = z.infer<typeof simularFichaSchema>;

export type AlertaFicha =
  | 'QUANTIDADE_INVALIDA'
  | 'QUANTIDADE_ZERO'
  | 'CUSTO_AUSENTE'
  | 'CUSTO_INVALIDO'
  | 'DESCRICAO_AUSENTE'
  /** Perda da linha fora do dominio (ex.: 100% sobre a entrada, rendimento 0, modo sem valor, perda em etapa). */
  | 'PERDA_INVALIDA';

/**
 * Avisos da linha ligados a etapa e cascata (E9), em campo proprio (`avisos`) para nao alargar `AlertaFicha`:
 * `CENTRO_INVALIDO` e `ETAPA_TIPO_INVALIDO` bloqueiam o salvar; `CPM_AUSENTE` (centro sem CPM ate o mes da data),
 * `CPM_ANTIGO` (usou um mes anterior ao ultimo salvo) nao bloqueiam; `CICLO_NA_FICHA` e `PROFUNDIDADE_FICHA_EXCEDIDA`
 * (cascata do insumo) fazem a gravacao ser recusada com 422.
 */
export type AlertaLinhaCusto =
  /** Sessao 35: semiacabado (item com ficha) cuja ficha escolhida nao existe mais: a linha segue com o custo GRAVADO (congelado) e entra em "Linhas que precisam de atencao". Nao bloqueia. */
  | 'CUSTO_CONGELADO_SEMIACABADO'
  | 'CENTRO_INVALIDO'
  | 'ETAPA_TIPO_INVALIDO'
  | 'CPM_AUSENTE'
  | 'CPM_ANTIGO'
  | 'CICLO_NA_FICHA'
  | 'PROFUNDIDADE_FICHA_EXCEDIDA'
  /** Insumo comprado sem custo de aquisicao vigente na data: nao bloqueia salvar, mas a ficha fica sem custo. */
  | 'AQUISICAO_AUSENTE'
  /** Subproduto que deixaria o custo liquido negativo (so no calculo da ficha; nao bloqueia o salvar da linha). */
  | 'SAIDA_INVALIDA';

/**
 * Regra do tipo do item que BLOQUEIA o salvar da linha (campo proprio, para nao alargar `AlertaLinhaCusto`):
 * `MAO_DE_OBRA_FORA_DO_PROCESSO` = linha MAO_DE_OBRA sem centro numa ficha de PRODUTO (o tempo do produto vive so nas etapas);
 * `COMPONENTE_NAO_PERMITIDO` = o item da linha nao compoe ficha (flag `compoeFichaTecnica` desligada) e nao e semiacabado;
 * `CENTRO_CONSUMO_FORA_DAS_ETAPAS` = o centro de consumo do material nao e o centro de nenhuma etapa do processo da ficha (Sessao 37).
 */
export type RestricaoLinhaFicha = 'MAO_DE_OBRA_FORA_DO_PROCESSO' | 'COMPONENTE_NAO_PERMITIDO' | 'CENTRO_CONSUMO_FORA_DAS_ETAPAS';

export interface LinhaSimuladaResponse {
  /** Posicao da linha no corpo (0-based). */
  indice: number;
  /** Custo unitario usado (o informado ou o resolvido pela API); null se ausente. So com custo.ver. */
  custoUnitario?: string | null;
  /** So com custo.ver. */
  custoTotal?: string | null;
  alertas: AlertaFicha[];
  /** Etapa/cascata (E9); ausente quando nao ha nenhum. Sem R$: o PCP recebe. */
  avisos?: AlertaLinhaCusto[];
  /** Sessao 49: motivo DA SUBFICHA quando o semiacabado da linha nao calcula (item da subficha + insumo/centro); sem R$. Ausente quando a subficha calcula. */
  motivos?: Array<{ code: AlertaLinhaCusto; message: string }>;
  /** Regra do tipo do item que impede gravar a linha; ausente quando nao ha. Sem R$. */
  restricoes?: RestricaoLinhaFicha[];
}

export interface SimularFichaResponse {
  linhas: LinhaSimuladaResponse[];
  /** Soma dos custos totais das linhas (qtd 1, SPEC 2.2/9.1), 6 casas. So com custo.ver (PCP nao recebe R$). */
  custoUnitario?: string;
  /** false se alguma linha nao poderia ser gravada (quantidade invalida, custo ausente/invalido, descricao vazia). */
  salvavel: boolean;
}

export const marcarEscolhidaSchema = z.object({ escolhida: z.boolean(), motivo: z.string().trim().min(1).max(200).optional() });
export type MarcarEscolhidaDto = z.infer<typeof marcarEscolhidaSchema>;

export interface ComponenteResponse {
  id: string;
  /** Fornecedor fixado na linha (null = a preferencial). */
  fornecedorId?: string | null;
  ordem: number;
  tipo: ComponenteTipoResposta;
  componenteItemId: string | null;
  /** Codigo do item componente (so em GET/PUT/nova-versao, que carregam o item). */
  componenteItemCodigo?: string | null;
  /** Sempre null desde a Sessao 6 (a aquisicao nao fica presa a linha). Mantido so por compatibilidade. */
  custoAquisicaoId: string | null;
  /** Etapa de roteiro: centro de custo (e `quantidade` = minutos). Estrutura, sem R$: o PCP recebe. */
  centroCustoId?: string | null;
  centroCustoCodigo?: string | null;
  /** Nome do centro da etapa (Sessao 37: a etapa se mostra como "<ordem>. <centro>"). */
  centroCustoNome?: string | null;
  /** Caminho do centro na arvore ("3.1"), derivado. */
  centroCustoCaminho?: string | null;
  /** Centro de consumo opcional de um material (informativo; fora do custo). */
  centroConsumoId?: string | null;
  centroConsumoCodigo?: string | null;
  centroConsumoCaminho?: string | null;
  descricao: string;
  unidade: string | null;
  quantidade: string;
  /** R$ da linha NA GRAVACAO (informativo; o custo valido e `custoNaData`): so com custo.ver. */
  custoUnitario?: string;
  /** So com custo.ver. */
  custoTotal?: string;
  /** Perda da linha (Sessao 16). */
  perdaModo?: PerdaModo;
  perdaValor?: string | null;
  /** Consumo que o motor usa: a quantidade com a perda da linha e o rendimento do processo ("1,000 + 20% sobre a saida = 1,200"). */
  consumoTotal?: string;
}

/** Estado do custo da ficha na data: `OK` ou o motivo de nao haver custo. Sem R$: o PCP recebe. */
export type EstadoCustoFicha = 'OK' | 'SEM_CPM' | 'AQUISICAO_AUSENTE' | 'CICLO_NA_FICHA' | 'PROFUNDIDADE_FICHA_EXCEDIDA' | 'SAIDA_INVALIDA';

/** Alerta de custo da ficha (sem valores). `CPM_ANTIGO` leva a competencia usada (AAAA-MM) e o centro. */
export interface AlertaCustoFicha {
  code: 'CPM_ANTIGO' | 'CPM_AUSENTE' | 'AQUISICAO_AUSENTE' | 'CICLO_NA_FICHA' | 'PROFUNDIDADE_FICHA_EXCEDIDA' | 'SAIDA_INVALIDA' | 'MAO_DE_OBRA_FORA_DO_PROCESSO' | 'CUSTO_CONGELADO_SEMIACABADO';
  message: string;
  /** Secao da ficha onde esta o problema (a tela a marca em vermelho). `SAIDAS` = subprodutos e residuos. */
  secao?: 'SAIDAS' | 'ETAPAS' | 'MATERIAIS';
  /** `MAO_DE_OBRA_FORA_DO_PROCESSO` e `CUSTO_CONGELADO_SEMIACABADO` (S35): a linha legada (posicao e descricao) que deve virar etapa; o custo dela continua o gravado. */
  ordem?: number;
  descricao?: string;
  centroCustoId?: string;
  centroCodigo?: string;
  competencia?: string;
}

/** No da arvore de semiacabados (insumo -> ficha escolhida dele), sem R$. `nivel` 1 = subficha direta da ficha. */
export interface ArvoreFichaNo {
  nivel: number;
  paiFichaId: string;
  itemId: string;
  itemCodigo: string;
  fichaId: string;
  versao: number;
  quantidade: string;
}

export interface EtapaCustoResponse {
  /** Ficha dona da etapa e nivel dela (1 = esta ficha, 2 = subficha direta...). */
  fichaId: string;
  nivel: number;
  ordem: number;
  centroCustoId: string;
  centroCodigo: string;
  minutos: string;
  /** Competencia (AAAA-MM) do CPM usado e o CPM (R$/min, 10 casas). */
  competencia: string;
  cpm: string;
  /** minutos x CPM, precisao cheia. */
  custo: string;
  /** O centro nao tem a competencia de referencia (ultimo mes salvo): usou o ultimo disponivel. */
  cpmAntigo: boolean;
}

export interface SubfichaCustoResponse extends ArvoreFichaNo {
  /** Custo de UMA unidade da subficha e da linha (quantidade x custo). */
  custo: string;
  custoLinha: string;
}

/** Custo da ficha NA DATA (so com custo.ver). Precisao cheia, sem arredondar entre niveis. */
export interface CustoNaDataResponse {
  data: string;
  /** Mes mais recente com CPM salvo entre os centros ativos ate o mes da data (referencia do "CPM antigo"). */
  ultimoMesSalvo: string | null;
  /** Linhas comuns (custo gravado), etapas desta ficha e semiacabados (cascata). `total` = soma dos tres. */
  materiais: string;
  processo: string;
  semiacabados: string;
  /** Custo UNITARIO do principal: (bruto - credito de subproduto + destinacao de residuo, ja rateado com os coprodutos) / quantidade de saida. */
  total: string;
  /** Sessao 31: a ficha produz N (saida principal) e o custo conjunto liquido do lote antes do rateio e da divisao por N. */
  quantidadeSaida?: string;
  conjunto?: string;
  /** Antes das saidas, e o credito e a destinacao (Sessao 17). */
  bruto?: string;
  credito?: string;
  destinacao?: string;
  etapas: EtapaCustoResponse[];
  subfichas: SubfichaCustoResponse[];
}

/** Custo de referencia do sistema de origem (XARP): valor gravado e data do mrp. Somente leitura; so com custo.ver. */
export interface ReferenciaXarpResponse {
  origem: 'XARP';
  custo: string;
  dataReferencia: string;
  /** `custoNaData.total` - `custo`, quando a ficha tem custo na data. */
  diferenca?: string | null;
}

/** Variacao do custo de hoje contra o marco da ficha (XARP na ficha importada; senao o custo gravado em `desde`). Calculada na API; so custo.ver. */
export interface VariacaoCustoResponse {
  origem: 'XARP' | 'GRAVADO';
  /** Custo no marco (6 casas). */
  baseCusto: string;
  /** Custo de hoje (`custoNaData.total`, 6 casas). */
  hoje: string;
  /** Data do marco (AAAA-MM-DD). */
  desde: string;
  /** hoje != base (a 6 casas): so entao a tela avisa. */
  variou: boolean;
  /** (hoje - base) / base x 100, 2 casas; null com base zero. */
  percentual: string | null;
}

export interface FichaResponse {
  /** Ficha vinda da estrutura do Protheus (em IMPORTAR): somente leitura; nova versao manual e bloqueada. */
  doProtheus?: boolean;
  id: string;
  itemId: string;
  versao: number;
  descricao: string | null;
  escolhida: boolean;
  /** Rendimento do processo (fracao 0 < r <= 1) e o grupo de insumos que o recebe (alem do tempo das etapas). Sessao 16. */
  rendimentoProcesso?: string | null;
  rendimentoGrupo?: RendimentoGrupo;
  /** Custo GRAVADO (cache da soma na gravacao). So com custo.ver. Para o custo valido, `custoNaData`. */
  custoUnitario?: string;
  /** So com custo.ver. */
  custoCalculadoEm?: string;
  /**
   * Sessao 49 (C): so em GET /fichas-tecnicas?itemId=...&comCustoHoje=true. Custo UNITARIO de hoje desta versao (o mesmo `custoNaData.total`
   * do cabecalho, aplicado a cada versao), SO com custo.ver. `null` = a versao nao calcula hoje; o motivo esta em `estadoCusto`/`alertasCusto`
   * (sem R$, o PCP recebe). `custoNaGravacao`/`custoNaGravacaoEm` repetem `custoUnitario`/`custoCalculadoEm` (cache da gravacao) com o nome
   * que a tela usa ("na gravacao: R$ X em dd/mm"); so custo.ver.
   */
  custoHoje?: string | null;
  custoNaGravacao?: string;
  custoNaGravacaoEm?: string;
  /** Usada em formacao/orcamento (snapshot): nao se edita, so se cria nova versao. Presente em listar/buscar/salvar. */
  emUso?: boolean;
  componentes?: ComponenteResponse[];
  /** Saida PRINCIPAL explicita (Sessao 31): a ficha produz N na unidade de custo do item. */
  quantidadeSaida?: string;
  criterioRateio?: CriterioRateioDto;
  /** Saidas extras (subproduto, residuo e coproduto). */
  saidas?: SaidaFichaResponse[];
  /** Tabela de rateio do custo conjunto (so com coproduto e custo.ver). */
  rateio?: RateioFichaResponse;
  /** Avisos ao salvar as saidas (ex.: o item do coproduto ja tem ficha propria). */
  avisosSaidas?: Array<{ code: 'COPRODUTO_ITEM_COM_FICHA'; message: string }>;
  /** So em GET /:id (e salvar/nova-versao): estado e alertas do custo na data, sem R$ (PCP recebe). */
  estadoCusto?: EstadoCustoFicha;
  alertasCusto?: AlertaCustoFicha[];
  /** Semiacabados em todos os niveis, sem R$ (PCP recebe). */
  arvore?: ArvoreFichaNo[];
  /** So com custo.ver; ausente quando `estadoCusto` != OK. */
  custoNaData?: CustoNaDataResponse;
  /** So com custo.ver; null quando a ficha nao veio do XARP. */
  referenciaXarp?: ReferenciaXarpResponse | null;
  /** So com custo.ver e custo na data; so em GET /:id. */
  variacaoCusto?: VariacaoCustoResponse;
  /** Secoes da tela (so em GET /:id e quem devolve a ficha completa): processo (etapas), materiais e semiacabados. */
  processo?: ProcessoFichaResponse;
  materiais?: MateriaisFichaResponse;
  semiacabados?: SemiacabadoFichaResponse[];
}

// ---------------------------------------------------------------------------------------------
// Secoes da ficha: processo, materiais, semiacabados e cascata (Sessao 5, item 4/5)
// ---------------------------------------------------------------------------------------------

/**
 * Etapa do processo produtivo (linha MAO_DE_OBRA com centro). `minutos` e o unico campo editavel; o CPM e SOMENTE LEITURA e
 * vem da grade de custo por minuto (vigente na data da consulta). Sem `custo.ver` as chaves `cpm`, `custo` (e `total` do
 * processo) nem existem; `competencia` (AAAA-MM) e `cpmAntigo` ficam (nao sao R$).
 */
export interface EtapaProcessoResponse {
  componenteId: string;
  ordem: number;
  descricao: string;
  centroCustoId: string;
  centroCodigo: string;
  centroNome: string;
  /** Caminho do centro analitico na arvore ("3.3.1"). */
  centroCaminho: string;
  minutos: string;
  /** Mes (AAAA-MM) do CPM usado; ausente quando o centro nao tem CPM ate a data. */
  competencia?: string;
  /** O centro nao tem o mes de referencia (ultimo mes salvo): usou o ultimo disponivel ("CPM antigo (mes)"). */
  cpmAntigo?: boolean;
  /** R$/min vigente na data (10 casas) e minutos x CPM. So com custo.ver. */
  cpm?: string;
  custo?: string;
}

export interface ProcessoFichaResponse {
  etapas: EtapaProcessoResponse[];
  /** Soma das etapas. So com custo.ver e com custo resolvido (estado OK). */
  total?: string;
}

export type OrigemCustoMaterial = 'AQUISICAO' | 'SEMIACABADO' | 'COPRODUTO' | 'INFORMADO';

/** Linha da secao Materiais: tudo que nao e etapa (material, aquisicao, outro, e mao de obra legada fora do processo). */
export interface MaterialFichaResponse {
  componenteId: string;
  ordem: number;
  tipo: ComponenteTipoResposta;
  descricao: string;
  itemId: string | null;
  itemCodigo: string | null;
  /** Tipo do item componente (lista unica da ficha: insumo, embalagem, servico de terceiro, produto). */
  itemTipo?: string | null;
  /** O item da linha e um SERVICO_TERCEIRO (entra na linha "Servicos de terceiros"). */
  servicoTerceiro?: boolean;
  /** Centro de consumo opcional (informativo). */
  centroConsumoId: string | null;
  centroConsumoCodigo: string | null;
  centroConsumoCaminho: string | null;
  quantidade: string;
  unidade: string | null;
  /** `SEMIACABADO` = custo da ficha escolhida do insumo na data (recursivo); `AQUISICAO` = custo de aquisicao; `INFORMADO` = digitado. */
  origemCusto: OrigemCustoMaterial;
  /** Semiacabado: a ficha escolhida do insumo usada no custo. */
  fichaId?: string | null;
  /** `MAO_DE_OBRA_FORA_DO_PROCESSO`: linha legada de mao de obra num PRODUTO (deve virar etapa; o custo gravado vale). */
  alertas?: Array<'MAO_DE_OBRA_FORA_DO_PROCESSO'>;
  /** So com custo.ver e custo resolvido: custo unitario efetivo e total da linha. */
  custoUnitario?: string;
  custoTotal?: string;
  /** AQUISICAO, so com custo.ver: aquisicao vigente do item NA DATA e se difere da gravada na linha (informativo; o custo usado e o gravado). */
  aquisicaoVigente?: string | null;
  aquisicaoDesatualizada?: boolean;
}

export interface MateriaisFichaResponse {
  itens: MaterialFichaResponse[];
  /** Soma de TODAS as linhas de material (inclui semiacabados). So com custo.ver e custo resolvido. `processo.total + materiais.total` = custo da ficha. */
  total?: string;
  /** Parte do total que vem de semiacabados. */
  totalSemiacabados?: string;
  /** Linha "Servicos de terceiros" (Sessao 16): parte do total que vem de itens SERVICO_TERCEIRO. So com custo.ver e custo resolvido. */
  totalServicosTerceiros?: string;
}

/** Semiacabado em qualquer nivel da cascata, com o caminho desde a ficha pedida. */
export interface SemiacabadoFichaResponse {
  nivel: number;
  paiFichaId: string;
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  fichaId: string;
  versao: number;
  quantidade: string;
  /** Codigos dos itens da ficha pedida ate este semiacabado (inclusive os dois), e os ids das fichas no mesmo caminho. */
  caminho: string[];
  caminhoFichaIds: string[];
  /** So com custo.ver e custo resolvido: custo de UMA unidade da subficha e da linha (quantidade x custo). */
  custo?: string;
  custoLinha?: string;
}

/** No da cascata: UMA ficha com o seu processo, os seus materiais, o custo do nivel e o total acumulado (com os filhos). */
export interface NoCascataResponse {
  /** 1 = a ficha pedida. */
  nivel: number;
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  fichaId: string;
  versao: number;
  /** Quantidade deste semiacabado por unidade do pai; null na raiz. */
  quantidade: string | null;
  caminho: string[];
  processo: ProcessoFichaResponse;
  materiais: MateriaisFichaResponse;
  /** So com custo.ver e custo resolvido. `custoNivel` = processo + materiais que NAO sao semiacabado; `totalAcumulado` = custo de uma unidade desta ficha (nivel + todos os filhos). */
  custoNivel?: string;
  totalAcumulado?: string;
  filhos: NoCascataResponse[];
}

/** GET /fichas-tecnicas/:id/cascata?data=AAAA-MM-DD */
export interface CascataResponse {
  fichaId: string;
  itemId: string;
  itemCodigo: string;
  data: string;
  /** `OK`, ou `SEM_CPM` (a arvore vem sem R$). Ciclo e profundidade excedida respondem 422 com o caminho. */
  estadoCusto: EstadoCustoFicha;
  alertasCusto: AlertaCustoFicha[];
  /** Niveis da arvore (ficha sem semiacabado = 1). */
  profundidade: number;
  raiz: NoCascataResponse;
}

/** GET /itens/:id/fichas/escolhas: historico de troca da ficha escolhida (DP4). Sem R$; `ficha.ver`. */
export interface FichaEscolhaResponse {
  id: string;
  itemId: string;
  fichaAnteriorId: string | null;
  fichaAnteriorVersao: number | null;
  fichaNovaId: string | null;
  fichaNovaVersao: number | null;
  usuarioId: string | null;
  usuarioNome: string | null;
  motivo: string | null;
  createdAt: string;
}

/**
 * `snapshot.refs.custoFicha` (formacao) e `snapshot.itensMeta[].custoFicha` (orcamento): o que foi usado para chegar ao
 * custo da ficha na data. Recalcular parte do `unitCost` do snapshot e nunca le CPM nem fichas vivas; isto e a prova.
 * Decimais como string, precisao cheia. `centroId`/`competencia` (AAAA-MM)/`cpm` (R$/min, 10 casas)/`minutos`.
 */
export interface RefsCustoFicha {
  fichaId: string;
  versao: number;
  /** Data (AAAA-MM-DD) em que o custo foi resolvido e o ultimo mes salvo entre os centros ativos ate ela. */
  data: string;
  ultimoMesSalvo: string | null;
  materiais: string;
  processo: string;
  semiacabados: string;
  total: string;
  /** Sessao 31: processo atribuivel a UMA unidade da saida (N e coproduto considerados) e o rateio de onde o custo veio. Snapshots antigos nao tem. */
  quantidadeSaida?: string;
  processoPorUnidade?: string;
  rateio?: RateioFichaResponse;
  etapas: Array<{
    fichaId: string;
    nivel: number;
    centroId: string;
    centroCodigo: string;
    competencia: string;
    cpm: string;
    minutos: string;
    custo: string;
    cpmAntigo: boolean;
  }>;
  /** Marca "CPM antigo (mes)": centros que usaram um mes anterior ao ultimo salvo. */
  cpmAntigo: Array<{ centroId: string; competencia: string }>;
  subfichas: Array<{ nivel: number; itemId: string; fichaId: string; quantidade: string; custo: string; paiFichaId: string }>;
  /**
   * Aquisicao USADA de cada insumo comprado da ficha (e das subfichas) na data: a vigencia (`custoAquisicaoId`, nulo se vinha de um
   * lote ainda nao aplicado), o custo unitario e o custo da linha. A versao da ficha nao guarda custo; esta e a prova do que
   * entrou no `unitCost`. Ausente em snapshots gravados antes da Sessao 6.
   */
  aquisicoes?: Array<{
    fichaId: string;
    nivel: number;
    itemId: string;
    itemCodigo: string;
    custoAquisicaoId: string | null;
    validFrom: string | null;
    quantidade: string;
    custoUnitario: string;
    custoLinha: string;
  }>;
}

// ---------- Saidas da ficha: subproduto e residuo (Sessao 17) ----------

/**
 * Saida extra da ficha, por unidade do principal. SUBPRODUTO: credito = quantidade x valor de recuperacao VIGENTE do item (cadastro com vigencia;
 * sem cadastro = 0), deduzido do custo do principal. RESIDUO: custo de destinacao = quantidade x `valorUnitario`, somado ao custo.
 * Ficha sem saidas = o custo de sempre. Sessao 31: as quantidades sao do LOTE (a ficha produz `quantidadeSaida` do principal); COPRODUTO = outra
 * saida PRINCIPAL que divide o custo conjunto (depois de deduzido o subproduto), por valor de venda relativo ou quantidade fisica.
 */
export const saidaClasse = z.enum(['SUBPRODUTO', 'RESIDUO', 'COPRODUTO']);
export type SaidaClasseDto = z.infer<typeof saidaClasse>;
const decimalSaida = z.string().regex(/^\d+(\.\d{1,8})?$/, 'Decimal não negativo, até 8 casas');
export const saidaSchema = z
  .object({
    classe: saidaClasse,
    itemId: cuid.optional(),
    descricao: z.string().trim().min(1).max(200),
    unidade: z.string().trim().min(1).max(10).optional(),
    quantidade: decimalSaida,
    valorUnitario: dinheiro.optional(),
  })
  .refine((v) => v.classe !== 'SUBPRODUTO' || (v.itemId !== undefined && v.valorUnitario === undefined), { message: 'Subproduto: informe o item (o valor vem do cadastro de recuperação)', path: ['itemId'] })
  .refine((v) => v.classe !== 'COPRODUTO' || (v.itemId !== undefined && v.valorUnitario === undefined), { message: 'Coproduto: informe o item (o custo vem do rateio do custo conjunto)', path: ['itemId'] })
  .refine((v) => v.classe !== 'RESIDUO' || v.itemId === undefined || v.valorUnitario === undefined, { message: 'Resíduo com item: o custo de destinação vem da aquisição vigente do item (não informe o valor); sem item, digite o valor', path: ['valorUnitario'] });

/** Critério do rateio do custo conjunto com coprodutos: valor de venda relativo (CPC 16, padrão) ou quantidade física. */
export const criterioRateioSchema = z.enum(['VALOR_VENDA', 'QUANTIDADE']);
export type CriterioRateioDto = z.infer<typeof criterioRateioSchema>;
const quantidadeSaidaSchema = z
  .string()
  .regex(/^\d+(\.\d{1,6})?$/, 'Decimal positivo, até 6 casas')
  .refine((v) => Number(v) > 0, 'A ficha precisa produzir mais que zero');
export const salvarSaidasSchema = z.object({
  saidas: z.array(saidaSchema).max(20),
  /** Saída PRINCIPAL explícita: "esta ficha produz N" (unidade de custo do item). Ausente = mantém o valor atual. */
  quantidadeSaida: quantidadeSaidaSchema.optional(),
  /** Critério do rateio (só vale com coproduto). Ausente = mantém. */
  criterioRateio: criterioRateioSchema.optional(),
});
export type SalvarSaidasDto = z.infer<typeof salvarSaidasSchema>;

/** De onde vem o custo de destinacao do resíduo: digitado na ficha, ou a aquisicao vigente do item (id, inicio da vigencia, fornecedor). */
export type OrigemValorResiduo =
  | { tipo: 'DIGITADO' }
  | { tipo: 'AQUISICAO'; custoAquisicaoId: string | null; validFrom: string | null; fornecedorId: string | null; preferencial: boolean };

export interface SaidaFichaResponse {
  id: string;
  ordem: number;
  classe: SaidaClasseDto;
  itemId: string | null;
  itemCodigo: string | null;
  descricao: string;
  unidade: string | null;
  quantidade: string;
  /** RESIDUO digitado (sem item): custo de destinacao por unidade da saida. So com custo.ver. */
  valorUnitario?: string | null;
  /**
   * RESIDUO com item comprado (Sessao 49, D): o custo de destinacao por unidade vem da AQUISICAO vigente do item na data (mesma cascata dos insumos:
   * fornecedor preferencial e validade). `valorVigente` = valor usado hoje (null = sem aquisicao vigente); `origemValor` diz de onde veio.
   * So com custo.ver.
   */
  valorVigente?: string | null;
  origemValor?: OrigemValorResiduo;
  /** RESIDUO com item sem aquisicao vigente na data: o motivo (sem R$; PCP tambem recebe). A ficha fica sem custo ate haver aquisicao. */
  alerta?: { code: 'AQUISICAO_AUSENTE'; message: string };
  /** SUBPRODUTO: valor de recuperacao vigente hoje (null = sem cadastro) e o credito por unidade do principal. So com custo.ver. */
  valorRecuperacao?: string | null;
  credito?: string;
}

/** Rateio do custo conjunto (Sessao 31): uma linha por saida PRINCIPAL (a do item da ficha e os coprodutos). Só com custo.ver. */
export interface RateioFichaLinha {
  itemId: string;
  itemCodigo: string;
  principal: boolean;
  quantidade: string;
  unidade: string | null;
  /** Preço publicado vigente usado no critério valor de venda (null = sem preço). */
  valorVenda: string | null;
  /** Peso do rateio (quantidade x valor de venda, ou a quantidade). */
  base: string;
  /** Fração (0 a 1) do custo conjunto. */
  percentual: string;
  /** Parcela do custo conjunto (centavos; a diferença fica na maior saída) e o custo por unidade da saída. */
  custo: string;
  custoUnitario: string;
}
export interface RateioFichaResponse {
  criterio: CriterioRateioDto;
  criterioAplicado: CriterioRateioDto;
  /** Cai para quantidade física quando alguma saída não tem preço publicado vigente. */
  fallback: boolean;
  aviso?: { code: 'RATEIO_SEM_VALOR_VENDA'; message: string };
  /** Custo conjunto líquido (bruto - crédito dos subprodutos + destinação dos resíduos). */
  conjunto: string;
  linhas: RateioFichaLinha[];
}

// ---------- Valor de recuperacao do subproduto (cadastro com vigencia) ----------

export const criarValorRecuperacaoSchema = z.object({
  itemId: cuid,
  validFrom: isoDate,
  /** R$ por unidade do item (a unidade do cadastro do item). */
  valor: dinheiro,
  /** Valor de exemplo (demo/simulacao): a tela marca como hipotetico. */
  hipotetico: z.boolean().default(false),
  motivo: z.string().trim().max(300).optional(),
});
export type CriarValorRecuperacaoDto = z.infer<typeof criarValorRecuperacaoSchema>;
export const listarValoresRecuperacaoSchema = z.object({ itemId: cuid.optional() });
export type ListarValoresRecuperacaoQuery = z.infer<typeof listarValoresRecuperacaoSchema>;
export interface ValorRecuperacaoResponse {
  id: string;
  itemId: string;
  itemCodigo: string;
  validFrom: string;
  validTo: string | null;
  valor: string;
  hipotetico: boolean;
  motivo: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------------------------
// Listagem de todas as fichas (Sessao 28, item 5): uma linha por item que tem (ou deveria ter) ficha
// ---------------------------------------------------------------------------------------------

export const SITUACOES_PAINEL_FICHA = ['ESCOLHIDA', 'SEM_ESCOLHIDA', 'SEM_FICHA'] as const;
export const situacaoPainelFicha = z.enum(SITUACOES_PAINEL_FICHA);
export type SituacaoPainelFicha = z.infer<typeof situacaoPainelFicha>;
export const ROTULO_SITUACAO_PAINEL_FICHA: Record<SituacaoPainelFicha, string> = { ESCOLHIDA: 'Com versão escolhida', SEM_ESCOLHIDA: 'Sem versão escolhida', SEM_FICHA: 'Sem ficha' };

/** GET /fichas-tecnicas/painel: itens PRODUTO/SERVICO ativos com a situacao da ficha. `categoriaId` inclui as subcategorias; `SEM` = sem categoria. */
export const painelFichasSchema = paginacao.extend({
  q: z.string().trim().min(1).max(100).optional(),
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
  situacao: situacaoPainelFicha.optional(),
  tipo: z.enum(['PRODUTO', 'SERVICO']).optional(),
});
export type PainelFichasQuery = z.infer<typeof painelFichasSchema>;

export interface PainelFichaLinha {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tipo: 'PRODUTO' | 'SERVICO';
  categoriaId: string | null;
  categoriaCaminho: string | null;
  situacao: SituacaoPainelFicha;
  /** Ficha escolhida (a vigente) ou, sem escolhida, a ultima versao. null = item sem ficha. */
  fichaId: string | null;
  versao: number | null;
  versoes: number;
  /** Custo na data de hoje da ficha ESCOLHIDA; so com custo.ver. null = sem custo calculavel (ver `estadoCusto`). */
  custo?: string | null;
  /** `OK` ou o motivo de nao haver custo (CPM_AUSENTE, AQUISICAO_AUSENTE, CICLO_NA_FICHA, SAIDA_INVALIDA...). Sem R$. */
  estadoCusto: 'OK' | 'SEM_FICHA_ESCOLHIDA' | string;
  atualizadaEm: string | null;
}
export interface PainelFichasResponse {
  itens: PainelFichaLinha[];
  total: number;
  limit: number;
  offset: number;
}

// ---------- Sessao 32: relatorio "Custo de referencia XARP x atual" (GET /fichas-tecnicas/custo-referencia, custo.ver) ----------

export const custoReferenciaQuerySchema = z.object({
  /** Data do custo atual (AAAA-MM-DD); padrao hoje. */
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** So linhas com |diferenca %| >= este valor (percentual, ex.: 5). Linhas sem referencia ficam de fora quando informado. */
  diferencaMinimaPct: z.coerce.number().min(0).max(100000).optional(),
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
  /** So a ficha escolhida (em uso) de cada item; padrao false = todas as versoes. */
  soEmUso: z.enum(['true', 'false']).optional(),
});
export type CustoReferenciaQuery = z.infer<typeof custoReferenciaQuerySchema>;

export interface CustoReferenciaLinha {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  categoriaId: string | null;
  categoriaCaminho: string | null;
  fichaId: string;
  versao: number;
  /** Ficha em uso (escolhida). */
  escolhida: boolean;
  /** Custo de referencia importado do XARP (null = ficha sem referencia). */
  referencia: { custo: string; dataReferencia: string; origem: string } | null;
  /** Custo na data pedida, calculado pelo servidor (null = sem custo calculavel; ver estadoCusto). */
  atual: { total: string; direto: string; indireto: string } | null;
  /** `OK` ou o motivo de nao haver custo (CPM_AUSENTE, AQUISICAO_AUSENTE...). */
  estadoCusto: string;
  /** atual - referencia (R$) e em % da referencia; null sem referencia ou sem custo atual (ou referencia 0 no %). */
  diferenca: string | null;
  diferencaPct: string | null;
  /**
   * Decomposicao da diferenca: direto (materiais, na data atual menos na data da referencia), indireto (processo = minutos x CPM, idem) e
   * outros (o resto: diferenca - direto - indireto = metodo e dados do legado, mais efeitos de semiacabados, saidas e rateio entre as datas). direto + indireto + outros = diferenca.
   * null quando o servidor nao consegue recalcular a ficha na data da referencia.
   */
  decomposicao: { direto: string; indireto: string; outros: string } | null;
  /**
   * Sessao 34: AAAA-MM da competencia de CPM usada quando NAO existe CPM na data da referencia (o processo da data da referencia e recalculado com o CPM da
   * competencia importada mais antiga do centro; os materiais seguem na data da referencia). null = decomposicao exata (ou sem decomposicao).
   */
  decomposicaoAproximadaCpm: string | null;
  /**
   * Sessao 35: AAAA-MM mais RECENTE entre as aquisicoes usadas no lugar da vigente na data da referencia (insumo sem aquisicao nessa data: vale a aquisicao importada mais
   * antiga dele). null = nenhum insumo precisou de aproximacao de preco.
   */
  decomposicaoAproximadaPreco: string | null;
}
export interface CustoReferenciaResponse {
  data: string;
  itens: CustoReferenciaLinha[];
  total: number;
}


// ---------------------------------------------------------------------------------------------
// "Usada em" (Sessao 49, E): onde um item e usado (fichas que o tem como componente ou saida, subindo pela cascata)
// ---------------------------------------------------------------------------------------------

/** Maximo de niveis da consulta "usada em" (nivel 1 = ficha que usa o item direto). Mesma constante da cascata (PROFUNDIDADE_MAXIMA). */
export const USADA_EM_MAX_NIVEIS = 10;

/**
 * GET /fichas-tecnicas/usada-em?itemId=...&soEscolhidas=true. `itemId` = o item produzido pela ficha OU o insumo/mercadoria comprado
 * (a mesma consulta serve a tela da ficha e o cadastro do insumo). `soEscolhidas` (padrao true): so fichas escolhidas; com false entram
 * todas as versoes (as nao escolhidas aparecem mas nao propagam para cima, pois a cascata so enxerga a escolhida).
 */
export const usadaEmQuerySchema = z.object({
  itemId: cuid,
  soEscolhidas: z.enum(['true', 'false']).transform((v) => v === 'true').optional().transform((v) => v ?? true),
});
export type UsadaEmQuery = z.input<typeof usadaEmQuerySchema>;
export type UsadaEmQueryParsed = z.output<typeof usadaEmQuerySchema>;

/**
 * Como o item entra na ficha: MATERIAL (insumo comprado), SEMIACABADO (item com ficha escolhida), RATEIO_COPRODUTO (linha COPRODUTO: o custo
 * da ficha vem do rateio da ficha do principal), SUBPRODUTO / RESIDUO / COPRODUTO (saida da ficha).
 */
export type UsadaEmRelacao = 'MATERIAL' | 'SEMIACABADO' | 'RATEIO_COPRODUTO' | 'SUBPRODUTO' | 'RESIDUO' | 'COPRODUTO';

/** Por que `custoLinha` nao veio (so quando e null; o motivo nao tem R$ e vai a quem nao ve custo). */
export type UsadaEmMotivoSemCusto = EstadoCustoFicha | 'SEM_CUSTO_DE_LINHA';

export interface UsadaEmLinha {
  /** 1 = a ficha usa o item pedido direto; 2+ = usa o item produzido por uma ficha do nivel anterior (cascata, subindo ate o topo). */
  nivel: number;
  /** A ficha que usa (id para o link), a versao, se e a escolhida e o item que ela produz. */
  fichaId: string;
  fichaVersao: number;
  escolhida: boolean;
  fichaItemId: string;
  fichaItemCodigo: string;
  fichaItemDescricao: string;
  relacao: UsadaEmRelacao;
  /** Id da linha (componente ou saida) dentro da ficha, e a posicao. */
  linhaId: string;
  ordem: number;
  descricao: string;
  /** Quantidade e unidade como gravadas na linha (consumo liquido; saida por unidade do principal). */
  quantidade: string;
  unidade: string | null;
  /** O item que a linha referencia: no nivel 1 e o pedido; nos demais, o semiacabado do nivel anterior por onde o impacto sobe. */
  viaItemId: string;
  viaItemCodigo: string;
  /**
   * SO COM custo.ver (a chave nao existe sem ela). Custo da linha HOJE (quantidade x custo, como o calculo da ficha: o de `CalculadoraCustoFicha`
   * para a ficha na data de hoje). SUBPRODUTO = credito (valor NEGATIVO); RESIDUO = custo de destinacao (positivo). `null` quando nao calcula
   * (ver `motivoSemCusto`).
   */
  custoLinha?: string | null;
  motivoSemCusto?: UsadaEmMotivoSemCusto;
}

export interface UsadaEmResponse {
  itemId: string;
  itemCodigo: string;
  soEscolhidas: boolean;
  maxNiveis: number;
  /** true se ha mais niveis acima do limite (a lista foi cortada em `maxNiveis`). */
  truncado: boolean;
  /** Ordenada por nivel, depois codigo do item da ficha, depois versao decrescente. */
  linhas: UsadaEmLinha[];
}
